/**
 * Утилиты для расчета метрик времени работы
 */

/**
 * Преобразует строку времени в минуты от начала дня
 * @param {string} timeString - Время в формате 'HH:MM'
 * @returns {number} - Минуты от начала дня
 */
export const timeToMinutes = (timeString) => {
    const [hours, minutes] = timeString.split(":").map(Number);
    return hours * 60 + minutes;
};

/**
 * Преобразует объект Date в минуты от начала дня
 * @param {Date} date - Объект даты
 * @returns {number} - Минуты от начала дня
 */
export const dateToMinutes = (date) => {
    return date.getHours() * 60 + date.getMinutes();
};

/**
 * Рассчитывает опоздание в минутах
 * @param {Date} enteredAt - Время входа (фактическое)
 * @param {string} startTime - Начало смены в формате 'HH:MM'
 * @returns {number} - Опоздание в минутах (0, если нет опоздания)
 */
export const calculateDelay = (enteredAt, startTime) => {
    if (!enteredAt) return null;

    const enteredMinutes = dateToMinutes(enteredAt);
    const startMinutes = timeToMinutes(startTime);

    return enteredMinutes > startMinutes ? enteredMinutes - startMinutes : 0;
};

/**
 * Рассчитывает переработку в минутах
 * @param {Date} exitedAt - Время выхода (фактическое)
 * @param {string} endTime - Конец смены в формате 'HH:MM'
 * @returns {number} - Переработка в минутах (0, если нет переработки)
 */
export const calculateOvertime = (exitedAt, endTime) => {
    if (!exitedAt) return null;

    const exitedMinutes = dateToMinutes(exitedAt);
    const endMinutes = timeToMinutes(endTime);

    return exitedMinutes > endMinutes ? exitedMinutes - endMinutes : 0;
};

/**
 * Рассчитывает ранний уход в минутах
 * @param {Date} exitedAt - Время выхода (фактическое)
 * @param {string} endTime - Конец смены в формате 'HH:MM'
 * @returns {number} - Ранний уход в минутах (0, если нет раннего ухода)
 */
export const calculateEarlyExit = (exitedAt, endTime) => {
    if (!exitedAt) return null;

    const exitedMinutes = dateToMinutes(exitedAt);
    const endMinutes = timeToMinutes(endTime);

    return exitedMinutes < endMinutes ? endMinutes - exitedMinutes : 0;
};

/**
 * Проверяет, является ли смена пропущенной
 * @param {Date} enteredAt - Время входа (фактическое)
 * @param {Date} exitedAt - Время выхода (фактическое)
 * @returns {boolean} - true если смена пропущена
 */
export const isMissedShift = (enteredAt, exitedAt) => {
    return !enteredAt && !exitedAt;
};

/**
 * Анализирует данные смены и возвращает все метрики
 */
export const analyzeWorkShift = (workShiftLog, shiftSchedule) => {
    if (!workShiftLog || !shiftSchedule) return null;

    const { enteredAt, exitedAt } = workShiftLog;
    const { startTime, endTime } = shiftSchedule;

    // Гарантируем Date-объекты
    const entered = enteredAt ? new Date(enteredAt) : null;
    const exited = exitedAt ? new Date(exitedAt) : null;

    const delayMinutes = calculateDelay(entered, startTime);
    const overtimeMinutes = calculateOvertime(exited, endTime);
    const earlyExitMinutes = calculateEarlyExit(exited, endTime);
    const missed = isMissedShift(entered, exited);

    return {
        delayMinutes,
        overtimeMinutes,
        earlyExitMinutes,
        missed,
        delay: delayMinutes, // Для обратной совместимости
        overtime: overtimeMinutes, // Для обратной совместимости
        earlyExit: earlyExitMinutes, // Для обратной совместимости
        hasDelay: delayMinutes > 0,
        hasOvertime: overtimeMinutes > 0,
        hasEarlyExit: earlyExitMinutes > 0,
    };
};
